import { withPostHog } from "./posthog";

// ---------------------------------------------------------------------------
// Partner referral code (rflkt/reflektlab#344)
// ---------------------------------------------------------------------------
//
// A partner shares `reflektlab.ai/?ref=CODE`. The code has to survive until
// the visitor books, writes or finishes the intake, which for a B2B prospect
// is often days later and in another tab. So unlike the UTMs (sessionStorage,
// first touch per session) it lives in localStorage for 90 days.
//
// Last touch wins: a new valid `?ref=` replaces the stored one. That is the
// same rule as atcom's middleware/referral.go, and it is the fair one when two
// partners introduce the same prospect: the one who sent them most recently
// gets the credit, and a human settles disputes in the CRM (rflkt/crm#813).
//
// Storage can be unavailable (private mode, blocked site data, the hydration
// check's headless Chrome). Every access is wrapped: losing a referral is
// acceptable, breaking the page is not.

const REF_PARAM = "ref";
const REF_STORAGE_KEY = "rflkt_ref";
export const REF_TTL_MS = 90 * 24 * 60 * 60 * 1000;

// Same rule as atcom's referral middleware and functions/api/_attribution.mjs.
const REF_RE = /^[A-Za-z0-9_-]{1,64}$/;

export function isValidRef(value: unknown): value is string {
  return typeof value === "string" && REF_RE.test(value);
}

// The code captured on this page load, for when storage is blocked: the
// referral then lasts for the visit instead of 90 days, rather than not at all.
let captured: string | undefined;

interface StoredRef {
  code: string;
  at: number;
}

function readStored(now: number): string | undefined {
  try {
    const raw = window.localStorage.getItem(REF_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<StoredRef>;
    if (
      !isValidRef(parsed.code) ||
      typeof parsed.at !== "number" ||
      now - parsed.at > REF_TTL_MS
    ) {
      window.localStorage.removeItem(REF_STORAGE_KEY);
      return undefined;
    }
    return parsed.code;
  } catch {
    return undefined;
  }
}

function writeStored(code: string, now: number): void {
  try {
    window.localStorage.setItem(
      REF_STORAGE_KEY,
      JSON.stringify({ code, at: now } as StoredRef),
    );
  } catch {
    // Storage blocked: the code still applies to this page view via PostHog.
  }
}

/**
 * Read `?ref=` from the landing URL and remember it. Call once on app load,
 * after PostHog is initialised. Returns the code now in effect, if any.
 *
 * A malformed `?ref=` is ignored and does not clear a valid stored one.
 */
export function captureReferral(
  search: string = typeof window !== "undefined" ? window.location.search : "",
  now: number = Date.now(),
): string | undefined {
  if (typeof window === "undefined") return undefined;

  const fromUrl = new URLSearchParams(search).get(REF_PARAM);
  let code: string | undefined;
  if (isValidRef(fromUrl)) {
    writeStored(fromUrl, now);
    code = fromUrl;
    captured = fromUrl;
  } else {
    code = readStored(now);
  }

  // A super property, so every event of this visit carries it, including
  // $pageview and book_call_clicked.
  if (code) {
    withPostHog((ph) => ph.register({ ref: code }));
  }
  return code;
}

/** The partner code in effect, or undefined (none, expired, or no storage). */
export function getReferral(now: number = Date.now()): string | undefined {
  if (typeof window === "undefined") return undefined;
  return readStored(now) ?? captured;
}

/** Test hook: forget the in-memory code. */
export function resetReferralForTests(): void {
  captured = undefined;
}
